import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowLeft, Send, Mail, User, Building, Phone, Plus, Edit, Trash2, 
  Save, FileText, Eye, Code, Search, X, CheckCircle, Users, Paperclip, Upload,
  DollarSign, CreditCard, Receipt, Inbox, Filter, Check, AlertCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AccountsEmailComposer = () => {
  const navigate = useNavigate();
  
  // Email Form State
  const [emailForm, setEmailForm] = useState({
    to: [],
    manualEmail: '',
    subject: '',
    body: '',
    isHtml: false,
    replyTo: 'care@netixfibernet.com'
  });
  
  // Customer Contacts State
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    mobile: '',
    officeName: ''
  });
  const [editingContact, setEditingContact] = useState(null);
  
  // Templates State
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    isHtml: false,
    category: 'billing'
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  
  // Campaign Statistics
  const [stats, setStats] = useState(null);
  
  // UI State
  const [activeTab, setActiveTab] = useState('compose');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('customerAdminToken');
    if (!token) {
      navigate('/customer-admin/login');
      return;
    }
    fetchData(token);
  }, [navigate]);

  const fetchData = async (token) => {
    setLoading(true);
    try {
      const [contactsRes, templatesRes, statsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/customer-admin/accounts-contacts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/customer-admin/accounts-templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/customer-admin/accounts-email-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (contactsRes.ok) {
        const contactsData = await contactsRes.json();
        setContacts(contactsData.contacts || []);
      }

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData.templates || []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats || null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultTemplates = async () => {
    const token = localStorage.getItem('customerAdminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/accounts-templates/initialize-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`✅ ${data.message}`);
        fetchData(token);
      }
    } catch (error) {
      console.error('Error initializing templates:', error);
    }
  };

  // Contact Management Functions
  const handleSaveContact = async () => {
    if (!contactForm.name || !contactForm.email) {
      toast.error('Name and email are required');
      return;
    }

    const token = localStorage.getItem('customerAdminToken');
    const url = editingContact 
      ? `${API_BASE_URL}/api/customer-admin/accounts-contacts/${editingContact.id}`
      : `${API_BASE_URL}/api/customer-admin/accounts-contacts`;
    
    const method = editingContact ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        toast.success(editingContact ? '✅ Contact updated!' : '✅ Contact added!');
        setShowContactModal(false);
        setContactForm({ name: '', email: '', mobile: '', officeName: '' });
        setEditingContact(null);
        fetchData(token);
      } else {
        toast.error('Failed to save contact');
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error('Error saving contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    const token = localStorage.getItem('customerAdminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/accounts-contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('🗑️ Contact deleted!');
        fetchData(token);
      } else {
        toast.error('Failed to delete contact');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Error deleting contact');
    }
  };

  // Template Management Functions
  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject) {
      toast.error('Template name and subject are required');
      return;
    }

    const token = localStorage.getItem('customerAdminToken');
    const url = editingTemplate 
      ? `${API_BASE_URL}/api/customer-admin/accounts-templates/${editingTemplate.id}`
      : `${API_BASE_URL}/api/customer-admin/accounts-templates`;
    
    const method = editingTemplate ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        toast.success(editingTemplate ? '✅ Template updated!' : '✅ Template saved!');
        setShowTemplateModal(false);
        setTemplateForm({ name: '', subject: '', body: '', isHtml: false, category: 'billing' });
        setEditingTemplate(null);
        fetchData(token);
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error saving template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    const token = localStorage.getItem('customerAdminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/accounts-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('🗑️ Template deleted!');
        fetchData(token);
      } else {
        toast.error('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error deleting template');
    }
  };

  const handleLoadTemplate = (template) => {
    setEmailForm({
      ...emailForm,
      subject: template.subject,
      body: template.body,
      isHtml: template.isHtml
    });
    setActiveTab('compose');
    toast.success('📋 Template loaded!');
  };

  // Email Functions
  const handleAddRecipient = (email) => {
    if (!emailForm.to.includes(email)) {
      setEmailForm({ ...emailForm, to: [...emailForm.to, email] });
      toast.success(`✅ Added ${email}`);
    }
  };

  const handleAddManualEmail = () => {
    const email = emailForm.manualEmail.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (!emailForm.to.includes(email)) {
        setEmailForm({ ...emailForm, to: [...emailForm.to, email], manualEmail: '' });
        toast.success(`✅ Added ${email}`);
      } else {
        toast.error('Email already added');
      }
    } else {
      toast.error('Please enter a valid email address');
    }
  };

  const handleRemoveRecipient = (email) => {
    setEmailForm({ ...emailForm, to: emailForm.to.filter(e => e !== email) });
  };

  const handleBulkSelectAll = () => {
    const allEmails = contacts.map(c => c.email);
    setEmailForm({ ...emailForm, to: allEmails });
    toast.success(`✅ Added ${allEmails.length} recipients`);
  };

  const handleSendEmail = async () => {
    if (emailForm.to.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }

    if (!emailForm.subject || !emailForm.body) {
      toast.error('Subject and body are required');
      return;
    }

    setSending(true);
    const token = localStorage.getItem('customerAdminToken');
    
    try {
      const formData = new FormData();
      formData.append('to', JSON.stringify(emailForm.to));
      formData.append('subject', emailForm.subject);
      formData.append('body', emailForm.body);
      formData.append('isHtml', emailForm.isHtml);
      formData.append('replyTo', emailForm.replyTo);
      
      // Add attachments
      attachments.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/accounts-send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      // Handle token expiry
      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('customerAdminToken');
        navigate('/customer-admin/login');
        return;
      }

      if (response.ok) {
        toast.success(`✅ Email sent to ${emailForm.to.length} recipient(s)!`);
        setEmailForm({ to: [], manualEmail: '', subject: '', body: '', isHtml: false, replyTo: 'care@netixfibernet.com' });
        setAttachments([]);
      } else {
        let errorMessage = 'Failed to send email';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status}`;
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Network error - please check your connection');
    } finally {
      setSending(false);
    }
  };

  const filteredContacts = contacts.filter(contact => 
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.officeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-yellow-50 to-red-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-pink-600 to-yellow-500 text-white p-6 shadow-2xl">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/customer-admin/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/30"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-8 w-8" />
                  <h1 className="text-4xl font-black">Accounts & Billing Email System</h1>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Mail className="h-4 w-4" />
                  <p className="text-white/90 font-semibold">From: accounts@netixfibernet.com</p>
                  <span className="mx-2">|</span>
                  <Inbox className="h-4 w-4" />
                  <p className="text-white/90 font-semibold">Reply-To: {emailForm.replyTo}</p>
                </div>
              </div>
            </div>
            <Sparkles className="h-16 w-16 opacity-60" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b-4 border-red-200 bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('compose')}
              className={`py-4 px-8 font-bold border-b-4 transition-all ${
                activeTab === 'compose'
                  ? 'border-red-600 text-red-600 bg-red-50 scale-105'
                  : 'border-transparent text-gray-600 hover:text-red-600 hover:bg-red-50'
              }`}
            >
              <Send className="inline h-5 w-5 mr-2" />
              Compose Email
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`py-4 px-8 font-bold border-b-4 transition-all ${
                activeTab === 'contacts'
                  ? 'border-pink-600 text-pink-600 bg-pink-50 scale-105'
                  : 'border-transparent text-gray-600 hover:text-pink-600 hover:bg-pink-50'
              }`}
            >
              <Users className="inline h-5 w-5 mr-2" />
              Customer Contacts ({contacts.length})
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-4 px-8 font-bold border-b-4 transition-all ${
                activeTab === 'templates'
                  ? 'border-yellow-600 text-yellow-700 bg-yellow-50 scale-105'
                  : 'border-transparent text-gray-600 hover:text-yellow-600 hover:bg-yellow-50'
              }`}
            >
              <FileText className="inline h-5 w-5 mr-2" />
              Email Templates ({templates.length})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Compose Tab */}
        {activeTab === 'compose' && (
          <div className="space-y-4">
            {/* Campaign Statistics - Compact */}
            {stats && (
              <div className="bg-gradient-to-r from-red-500 to-yellow-500 rounded-xl shadow-lg p-4 text-white">
                <div className="grid grid-cols-5 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-black">{stats.totalSent}</p>
                    <p className="text-xs font-semibold">Total Sent</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black">{stats.thisMonth}</p>
                    <p className="text-xs font-semibold">This Month</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black">{stats.today}</p>
                    <p className="text-xs font-semibold">Today</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black">{stats.totalRecipients}</p>
                    <p className="text-xs font-semibold">Recipients</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black">{stats.averageRecipientsPerEmail}</p>
                    <p className="text-xs font-semibold">Avg/Email</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-xl p-6 border-2 border-red-200 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                  <Send className="h-6 w-6 text-red-600" />
                  Compose Email
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setBulkMode(!bulkMode)}
                    size="sm"
                    className={`${bulkMode ? 'bg-gradient-to-r from-pink-600 to-red-600' : 'bg-gray-500'}`}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    {bulkMode ? 'Bulk ON' : 'Bulk OFF'}
                  </Button>
                </div>
              </div>

              {/* Template Selector - NEW */}
              <div className="bg-gradient-to-r from-yellow-50 to-pink-50 border-2 border-yellow-300 rounded-lg p-4">
                <Label className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-yellow-600" />
                  Quick Start: Select a Template
                </Label>
                <div className="flex gap-2">
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => {
                      const template = templates.find(t => t.id === e.target.value);
                      if (template) {
                        setEmailForm({
                          ...emailForm,
                          subject: template.subject,
                          body: template.body,
                          isHtml: template.isHtml
                        });
                        setSelectedTemplateId(e.target.value);
                        toast.success('📋 Template loaded!');
                      }
                    }}
                    className="flex-1 border-2 border-yellow-400 rounded-lg p-2 text-sm"
                  >
                    <option value="">-- Select a Ready-made Template --</option>
                    {templates.filter(t => t.isDefault).map(template => (
                      <option key={template.id} value={template.id}>
                        🎯 {template.name} ({template.category})
                      </option>
                    ))}
                    <option disabled>---</option>
                    {templates.filter(t => !t.isDefault).map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                  {templates.filter(t => t.isDefault).length === 0 && (
                    <Button
                      onClick={initializeDefaultTemplates}
                      size="sm"
                      className="bg-gradient-to-r from-green-600 to-emerald-600"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Load Templates
                    </Button>
                  )}
                </div>
              </div>

            {bulkMode && (
              <div className="bg-gradient-to-r from-pink-100 to-yellow-100 border-2 border-pink-300 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-pink-700" />
                    <span className="text-sm font-bold text-pink-900">Bulk Mode: Send to multiple customers</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleBulkSelectAll}
                      size="sm"
                      className="bg-gradient-to-r from-pink-600 to-red-600"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      All ({contacts.length})
                    </Button>
                    <Button
                      onClick={() => setEmailForm({ ...emailForm, to: [] })}
                      size="sm"
                      className="bg-gray-600"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Recipients - Compact */}
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 rounded-lg p-4">
              <Label className="text-sm font-bold text-red-900 mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Recipients ({emailForm.to.length})
              </Label>
              <div className="flex flex-wrap gap-1 mb-3 max-h-24 overflow-y-auto">
                {emailForm.to.map((email, idx) => (
                  <div key={idx} className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs">
                    <span className="font-semibold">{email}</span>
                    <button onClick={() => handleRemoveRecipient(email)}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={emailForm.manualEmail}
                  onChange={(e) => setEmailForm({ ...emailForm, manualEmail: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddManualEmail()}
                  placeholder="Type email and press Enter"
                  className="flex-1 border-2 border-red-300 py-2 text-sm"
                />
                <Button
                  onClick={handleAddManualEmail}
                  size="sm"
                  className="bg-gradient-to-r from-red-600 to-pink-600"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Email
                </Button>
                <Button
                  onClick={() => setActiveTab('contacts')}
                  size="sm"
                  className="bg-gradient-to-r from-green-600 to-emerald-600"
                >
                  <Users className="h-3 w-3 mr-1" />
                  Select Customer
                </Button>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                💡 Type email manually or click "Select Customer" to choose from saved contacts
              </p>
            </div>

            {/* Reply-To & Subject in one row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-bold text-yellow-900 mb-1 flex items-center gap-1">
                  <Inbox className="h-3 w-3" />
                  Reply-To
                </Label>
                <Input
                  value={emailForm.replyTo}
                  onChange={(e) => setEmailForm({ ...emailForm, replyTo: e.target.value })}
                  placeholder="care@netixfibernet.com"
                  className="border-2 border-yellow-300 py-2 text-sm"
                />
              </div>
              <div>
                <Label className="text-sm font-bold text-pink-900 mb-1 flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Subject
                </Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="Enter email subject"
                  className="border-2 border-pink-300 py-2 text-sm"
                />
              </div>
            </div>

            {/* HTML Toggle - Compact */}
            <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-100 to-pink-100 border-2 border-yellow-300 rounded-lg p-2">
              <input
                type="checkbox"
                id="htmlMode"
                checked={emailForm.isHtml}
                onChange={(e) => setEmailForm({ ...emailForm, isHtml: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <Label htmlFor="htmlMode" className="text-sm font-bold flex items-center gap-1 cursor-pointer">
                <Code className="h-4 w-4 text-yellow-700" />
                Enable HTML Mode
              </Label>
            </div>

            {/* Body - Compact */}
            <div>
              <Label className="text-sm font-bold text-red-900 mb-1 flex items-center gap-1">
                <FileText className="h-3 w-3" />
                Email Content
              </Label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                placeholder={emailForm.isHtml ? "Enter HTML content..." : "Enter your email message..."}
                rows={10}
                className="border-2 border-red-300 focus:border-red-600 font-mono text-sm"
              />
            </div>

            {/* Attachments - Compact */}
            <div>
              <Label className="text-sm font-bold text-pink-900 mb-1 flex items-center gap-1">
                <Paperclip className="h-3 w-3" />
                Attachments
              </Label>
              <div className="border-2 border-dashed border-pink-300 rounded-lg p-4 text-center bg-gradient-to-r from-pink-50 to-yellow-50 hover:from-pink-100 hover:to-yellow-100 transition-all">
                <input
                  type="file"
                  id="email-attachments"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setAttachments(files);
                    toast.success(`✅ ${files.length} file(s) selected`);
                  }}
                  className="hidden"
                />
                <label htmlFor="email-attachments" className="cursor-pointer">
                  <Upload className="h-8 w-8 text-pink-600 mx-auto mb-1" />
                  <p className="text-sm font-bold text-pink-900">Click to upload files</p>
                  <p className="text-xs text-gray-600">Invoices, receipts, documents</p>
                </label>
              </div>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gradient-to-r from-yellow-100 to-pink-100 border border-yellow-300 p-2 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Paperclip className="h-3 w-3 text-yellow-700" />
                        <span className="font-semibold text-gray-900 text-xs">{file.name}</span>
                        <span className="text-xs text-gray-600">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                      <button onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))} className="text-red-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Send Button - Compact */}
            <div className="flex justify-end gap-2 pt-4 border-t-2 border-red-200">
              <Button
                onClick={() => setEmailForm({ to: [], manualEmail: '', subject: '', body: '', isHtml: false, replyTo: 'care@netixfibernet.com' })}
                size="sm"
                className="bg-gray-500 hover:bg-gray-600"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sending || emailForm.to.length === 0}
                className="bg-gradient-to-r from-red-600 via-pink-600 to-yellow-500 hover:from-red-700 hover:via-pink-700 hover:to-yellow-600 font-bold hover:scale-105 transition-transform"
              >
                {sending ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send to {emailForm.to.length} Recipient(s)
                  </>
                )}
              </Button>
            </div>
          </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-pink-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-black text-gray-900 flex items-center gap-3">
                  <div className="bg-gradient-to-r from-pink-600 to-red-600 rounded-full p-3">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  Customer Contact Management
                </h2>
                <Button
                  onClick={() => {
                    setShowContactModal(true);
                    setEditingContact(null);
                    setContactForm({ name: '', email: '', mobile: '', officeName: '' });
                  }}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:scale-105 transition-transform px-6 py-6 text-lg font-bold"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add New Contact
                </Button>
              </div>

              {/* Search */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-4 top-4 h-6 w-6 text-pink-600" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email, or office..."
                    className="pl-14 py-6 text-lg border-4 border-pink-300 focus:border-pink-600"
                  />
                </div>
              </div>

              {/* Contacts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredContacts.map(contact => (
                  <div key={contact.id} className="bg-gradient-to-br from-pink-50 to-yellow-50 border-4 border-pink-300 rounded-xl p-6 hover:shadow-2xl hover:scale-105 transition-all">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-r from-pink-600 to-red-600 rounded-full p-3">
                          <User className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="font-black text-xl text-gray-900">{contact.name}</h3>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingContact(contact);
                            setContactForm(contact);
                            setShowContactModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:scale-110 transition-transform"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-600 hover:text-red-800 p-2 hover:scale-110 transition-transform"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm text-gray-700 mb-4">
                      <div className="flex items-center gap-2 bg-white p-2 rounded-lg">
                        <Mail className="h-4 w-4 text-pink-600" />
                        <span className="font-semibold">{contact.email}</span>
                      </div>
                      {contact.mobile && (
                        <div className="flex items-center gap-2 bg-white p-2 rounded-lg">
                          <Phone className="h-4 w-4 text-pink-600" />
                          <span>{contact.mobile}</span>
                        </div>
                      )}
                      {contact.officeName && (
                        <div className="flex items-center gap-2 bg-white p-2 rounded-lg">
                          <Building className="h-4 w-4 text-pink-600" />
                          <span>{contact.officeName}</span>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        handleAddRecipient(contact.email);
                        setActiveTab('compose');
                      }}
                      size="sm"
                      className="w-full bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700 py-3 font-bold"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add to Email
                    </Button>
                  </div>
                ))}
              </div>

              {filteredContacts.length === 0 && (
                <div className="text-center py-16 text-gray-500">
                  <Users className="h-24 w-24 mx-auto mb-6 opacity-30" />
                  <p className="text-2xl font-bold">No contacts found</p>
                  <p className="text-lg mt-2">Add your first customer contact to get started</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-yellow-300">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-black text-gray-900 flex items-center gap-3">
                  <div className="bg-gradient-to-r from-yellow-500 to-red-600 rounded-full p-3">
                    <FileText className="h-8 w-8 text-white" />
                  </div>
                  Email Template Library
                </h2>
                <Button
                  onClick={() => {
                    setShowTemplateModal(true);
                    setEditingTemplate(null);
                    setTemplateForm({ name: '', subject: '', body: '', isHtml: false, category: 'billing' });
                  }}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:scale-105 transition-transform px-6 py-6 text-lg font-bold"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create Template
                </Button>
              </div>

              {/* Templates Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {templates.map(template => (
                  <div key={template.id} className="bg-gradient-to-br from-yellow-50 to-red-50 border-4 border-yellow-300 rounded-xl p-6 hover:shadow-2xl hover:scale-105 transition-all">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-r from-yellow-500 to-red-600 rounded-full p-2">
                          <FileText className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-black text-xl text-gray-900">{template.name}</h3>
                          <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full font-bold">
                            {template.category || 'General'}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setPreviewTemplate(template);
                            setShowTemplatePreview(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:scale-110 transition-transform"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingTemplate(template);
                            setTemplateForm(template);
                            setShowTemplateModal(true);
                          }}
                          className="text-green-600 hover:text-green-800 p-2 hover:scale-110 transition-transform"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="text-red-600 hover:text-red-800 p-2 hover:scale-110 transition-transform"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3 mb-4">
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-xs text-gray-600 font-semibold">Subject:</p>
                        <p className="font-bold text-gray-900">{template.subject}</p>
                      </div>
                      {template.isHtml && (
                        <span className="inline-flex items-center gap-1 bg-purple-200 text-purple-900 px-3 py-1 rounded-full text-sm font-bold">
                          <Code className="h-4 w-4" />
                          HTML Template
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={() => handleLoadTemplate(template)}
                      size="sm"
                      className="w-full bg-gradient-to-r from-yellow-500 to-red-600 hover:from-yellow-600 hover:to-red-700 py-3 font-bold"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Use This Template
                    </Button>
                  </div>
                ))}
              </div>

              {templates.length === 0 && (
                <div className="text-center py-16 text-gray-500">
                  <FileText className="h-24 w-24 mx-auto mb-6 opacity-30" />
                  <p className="text-2xl font-bold">No templates found</p>
                  <p className="text-lg mt-2">Create your first email template to save time</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Contact Modal - REDESIGNED */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 border-4 border-pink-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-r from-pink-600 to-red-600 rounded-full p-3">
                <User className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-3xl font-black text-gray-900">
                {editingContact ? 'Edit Customer Contact' : 'Add New Customer Contact'}
              </h3>
            </div>
            <div className="space-y-5">
              <div>
                <Label className="text-lg font-bold text-pink-900 mb-2">Customer Name *</Label>
                <Input
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder="Enter full name"
                  className="border-3 border-pink-300 focus:border-pink-600 py-3 text-lg"
                />
              </div>
              <div>
                <Label className="text-lg font-bold text-pink-900 mb-2">Email Address *</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="customer@example.com"
                  className="border-3 border-pink-300 focus:border-pink-600 py-3 text-lg"
                />
              </div>
              <div>
                <Label className="text-lg font-bold text-pink-900 mb-2">Mobile Number</Label>
                <Input
                  value={contactForm.mobile}
                  onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })}
                  placeholder="9876543210"
                  className="border-3 border-pink-300 focus:border-pink-600 py-3 text-lg"
                />
              </div>
              <div>
                <Label className="text-lg font-bold text-pink-900 mb-2">Office/Company Name</Label>
                <Input
                  value={contactForm.officeName}
                  onChange={(e) => setContactForm({ ...contactForm, officeName: e.target.value })}
                  placeholder="Office or company name"
                  className="border-3 border-pink-300 focus:border-pink-600 py-3 text-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-8 pt-6 border-t-4 border-pink-200">
              <Button
                onClick={() => {
                  setShowContactModal(false);
                  setEditingContact(null);
                }}
                className="bg-gray-500 hover:bg-gray-600 px-8 py-4 text-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveContact}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 px-10 py-4 text-lg font-bold"
              >
                <Save className="h-5 w-5 mr-2" />
                Save Contact
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal - REDESIGNED */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto border-4 border-yellow-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-r from-yellow-500 to-red-600 rounded-full p-3">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-3xl font-black text-gray-900">
                {editingTemplate ? 'Edit Email Template' : 'Create New Email Template'}
              </h3>
            </div>
            <div className="space-y-5">
              <div>
                <Label className="text-lg font-bold text-yellow-900 mb-2">Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Monthly Invoice, Payment Reminder"
                  className="border-3 border-yellow-300 focus:border-yellow-600 py-3 text-lg"
                />
              </div>
              <div>
                <Label className="text-lg font-bold text-yellow-900 mb-2">Category</Label>
                <select
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                  className="w-full border-3 border-yellow-300 rounded-lg p-3 text-lg"
                >
                  <option value="billing">Billing</option>
                  <option value="payment">Payment</option>
                  <option value="invoice">Invoice</option>
                  <option value="reminder">Reminder</option>
                  <option value="notification">Notification</option>
                </select>
              </div>
              <div>
                <Label className="text-lg font-bold text-yellow-900 mb-2">Subject Line *</Label>
                <Input
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="Email subject"
                  className="border-3 border-yellow-300 focus:border-yellow-600 py-3 text-lg"
                />
              </div>
              <div className="flex items-center gap-4 bg-gradient-to-r from-yellow-100 to-pink-100 border-3 border-yellow-300 rounded-xl p-4">
                <input
                  type="checkbox"
                  id="templateHtml"
                  checked={templateForm.isHtml}
                  onChange={(e) => setTemplateForm({ ...templateForm, isHtml: e.target.checked })}
                  className="w-6 h-6 rounded"
                />
                <Label htmlFor="templateHtml" className="text-lg font-bold cursor-pointer flex items-center gap-2">
                  <Code className="h-5 w-5 text-yellow-700" />
                  HTML Template (Advanced)
                </Label>
              </div>
              <div>
                <Label className="text-lg font-bold text-yellow-900 mb-2">Email Content</Label>
                <Textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  placeholder={templateForm.isHtml ? "Enter HTML content..." : "Enter email content..."}
                  rows={14}
                  className="border-3 border-yellow-300 focus:border-yellow-600 font-mono text-base"
                />
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-8 pt-6 border-t-4 border-yellow-200">
              <Button
                onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                }}
                className="bg-gray-500 hover:bg-gray-600 px-8 py-4 text-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTemplate}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-10 py-4 text-lg font-bold"
              >
                <Save className="h-5 w-5 mr-2" />
                Save Template
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {showTemplatePreview && previewTemplate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto border-4 border-blue-300">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-3xl font-black text-gray-900">Template Preview: {previewTemplate.name}</h3>
              <button onClick={() => setShowTemplatePreview(false)} className="hover:scale-110 transition-transform">
                <X className="h-8 w-8 text-gray-600" />
              </button>
            </div>
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-4 border-gray-200 rounded-xl p-6">
              <div className="mb-6">
                <p className="text-sm text-gray-600 font-semibold">Subject:</p>
                <p className="text-xl font-black text-gray-900">{previewTemplate.subject}</p>
              </div>
              <div className="border-t-4 border-gray-300 pt-6">
                {previewTemplate.isHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: previewTemplate.body }} className="prose max-w-none" />
                ) : (
                  <pre className="whitespace-pre-wrap font-sans text-base text-gray-800">{previewTemplate.body}</pre>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsEmailComposer;
